import React, { type RefObject, useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import Router from 'next/router';
import { gql } from '@soundxyz/gql-string';
import { isEditReleaseDisabled } from '@components/Dashboard/DraftsAndReleases/Releases/helpers';
import { clearAllLocalData } from '@components/Dashboard/Upload/helpers';
import { useLikeRelease } from '@components/Likes/useLikeRelease';
import { MintComponent } from '@components/Song/MintComponents';
import { ActionBarV2 } from '@components/basics/ActionBar/ActionBarV2';
import { AddToQueueMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToQueueMenuItem';
import { AddToShelfMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToShelfMenuItem';
import { DownloadAudioMenuItem } from '@components/basics/Dropdowns/MenuItems/DownloadAudioMenuItem';
import { EditReleaseMenuItem } from '@components/basics/Dropdowns/MenuItems/EditReleaseMenuItem';
import { TabList, Tabs, TabTrigger } from '@components/main-components/tabs/Tab';
import { CommentModalV2 } from '@components/modals/CommentV2';
import { FeedPostModal } from '@components/modals/FeedPostModal';
import { UnlockFeedModal } from '@components/modals/FeedPostModal/UnlockFeedModal';
import { ShareModalRelease } from '@components/modals/ShareModal/ShareModalRelease';
import { DisplayToggle } from '@components/shared';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalType, useModal } from '@context/modal';
import { ParamsStore, useParams } from '@context/params';
import type { CreditRoleType, FragmentType, SongViewFragment } from '@graphql/reactQuery';
import {
  FeedPostModalMainSoundFragmentDoc,
  getFragment,
  PlaylistType,
  setQueryData,
  SongViewFragmentDoc,
  WaveformExpandedPlayerDocument,
} from '@graphql/reactQuery';
import { useBackendUserPersistedData } from '@hooks/useBackendUserPersistedData';
import { useEditPath } from '@hooks/useEditPath';
import { useNftOwnershipData } from '@hooks/useNftOwnershipData';
import { usePlayButton } from '@hooks/usePlayButton';
import { usePrefetchEmbed } from '@hooks/usePrefetchEmbed';
import { useReleasePartyConfetti } from '@hooks/useReleasePartyConfetti';
import { useSafeLayoutEffect } from '@hooks/useSafeLayoutEffect';
import { trackClickEvent } from '@lib/analytics';
import { useAudioController } from '@store/audio/AudioController';
import { RELEASE_INFO_STEP_ID } from '@utils/constants';
import { ReleaseActivityFeed } from './Activity/ReleaseActivityFeed';
import type { TabChoice } from './Activity/store';
import { TAB_CHOICES } from './Activity/store';
import { Audience } from './Audience';
import { BehindTheMusicSection } from './BehindTheMusic';
import { Comments } from './Comments';
import { ContractDetailsSection } from './Contract';
import { DiscoveredOnCarousel } from './DiscoveredOnCarousel';
import { SongHero } from './Hero/SongHero';
import { OverviewTab } from './Overview';
import { PeopleAlsoCollectedCarousel } from './PeopleAlsoCollectedCarousel';
import { RewardComponent } from './Reward';
import { ShareAndEarnButton } from './ShareAndEarnButton';
import { CreditSplitsSection } from './Splits';
import {
  InfoContainer,
  LeftContainer,
  RightContainer,
  Sections,
  SharedWrapper,
  SongPageContainer,
  StyledTabContent,
  Wrapper,
} from './shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment SongView on Release {
    id
    contractAddress
    draftId
    type
    titleSlug
    auctionContractType
    listeningParty
    royaltyBps
    # for tiers we get from the listing
    listing {
      ... on TieredEditionListing {
        royaltyBPS
      }
    }
    vipCoverImage {
      id
    }
    track {
      id
      duration
    }
    collectorsCount
    totalMinted
    saleDetails {
      totalVolume
    }
    mintStartTime
    mintStartTimestamp
    artist {
      id
      userId
      soundHandle
      ...MoreFromArtistCarouselInfo
    }
    creditSplit {
      id
      splitAddress
      creditAllocations {
        id
        roles
        percent
        owner {
          id
          artist {
            id
          }
          avatar {
            id
            url
          }
          username
          publicAddress
          webappUri
        }
      }
    }
    chainId

    canCollectorsDownloadAudio
    ...SongHero
    ...BehindTheMusicSection
    ...AudienceSongInfo
    ...RewardsSongInfo
    ...ReleaseShareAndEarn
    ...WaveformExpandedPlayerInfo
    ...MintComponent
    ...ReleaseReferrableInfo
    ...CollectModal
    ...FeedPostModalMainSound
  }
`);

function TabContent({
  activeTab,
  releaseFrag,
  tabRef,
  setActiveTab,
  tabParentRef,
}: {
  activeTab: TabChoice;
  releaseFrag: SongViewFragment;
  tabRef?: RefObject<HTMLDivElement>;
  tabParentRef?: RefObject<HTMLDivElement>;
  setActiveTab?: (tab: TabChoice) => void;
}) {
  const splits = useMemo(
    () =>
      releaseFrag.creditSplit?.creditAllocations.map(allocation => ({
        ownerAddress: allocation.owner.publicAddress,
        avatarUrl: allocation.owner?.avatar?.url,
        ...allocation,
        roles: allocation.roles as Array<CreditRoleType>,
      })),
    [releaseFrag.creditSplit?.creditAllocations],
  );

  switch (activeTab) {
    case 'details':
      return (
        <StyledTabContent value={TAB_CHOICES.DETAILS}>
          <SharedWrapper>
            <BehindTheMusicSection releaseFrag={releaseFrag} />
          </SharedWrapper>
          <SharedWrapper>
            <Audience releaseFrag={releaseFrag} isFullPage={false} tabsTopOffset={undefined} />
          </SharedWrapper>
          <SharedWrapper>
            <ShareAndEarnButton isBanner releaseFrag={releaseFrag} />
          </SharedWrapper>
          <RewardComponent releaseFrag={releaseFrag} />
          <div className="box-border flex flex-col">
            {!!splits && (
              <SharedWrapper>
                <CreditSplitsSection splits={splits} />
              </SharedWrapper>
            )}
            <SharedWrapper>
              <ContractDetailsSection
                mintStartTime={releaseFrag.mintStartTime}
                contractAddress={releaseFrag.contractAddress}
                splitsContractAddress={releaseFrag.creditSplit?.splitAddress}
                chainId={releaseFrag.chainId}
                mints={releaseFrag.totalMinted}
                totalVolume={releaseFrag.saleDetails?.totalVolume}
                uniqueCollectors={releaseFrag.collectorsCount}
                royaltyBps={releaseFrag.listing?.royaltyBPS ?? releaseFrag.royaltyBps}
              />
            </SharedWrapper>
          </div>
        </StyledTabContent>
      );
    case 'activity':
      return (
        <StyledTabContent value={TAB_CHOICES.ACTIVITY}>
          <SharedWrapper>
            <ReleaseActivityFeed releaseId={releaseFrag.id} />
          </SharedWrapper>
        </StyledTabContent>
      );
    case 'audience':
      return (
        <StyledTabContent value={TAB_CHOICES.AUDIENCE} className="!mt-0 !pt-0">
          <SharedWrapper>
            <Audience releaseFrag={releaseFrag} tabsTopOffset={undefined} isFullPage />
          </SharedWrapper>
        </StyledTabContent>
      );
    case 'comments':
      return (
        <StyledTabContent value={TAB_CHOICES.COMMENTS} className="pt-0">
          <SharedWrapper>
            <Comments
              releaseFrag={releaseFrag}
              tabsHeight={tabRef?.current?.clientHeight}
              tabsParentHeight={tabParentRef?.current?.clientHeight}
            />
          </SharedWrapper>
        </StyledTabContent>
      );

    case 'overview':
      return (
        <OverviewTab
          setActiveTab={setActiveTab}
          releaseFrag={releaseFrag}
          tabsTopOffset={tabParentRef?.current?.offsetTop}
        />
      );
  }
}

function Actions({ release }: { release: SongViewFragment }) {
  const { openModal } = useModal();
  const { authUser } = useAuth();
  usePrefetchEmbed({
    releaseId: release.id,
  });
  const { login } = useConnectedAuth();

  const editPath = useEditPath({
    auctionContractType: release.auctionContractType,
    draftId: release.draftId,
    releaseId: release.id,
    releaseType: release.type,
    stepId: RELEASE_INFO_STEP_ID,
  });

  const showEditRelease = editPath !== 'DISABLED';

  const { isReleaseOwner } = useNftOwnershipData({
    releaseId: release.id,
  });

  const { id: releaseId, track, type: releaseType } = release;
  const trackId = track.id;

  const hasAudioAccess = releaseType !== 'ALBUM';

  const { isLiked, onClick: onLikeButtonClick } = useLikeRelease({
    id: releaseId,
    source: 'Song Page',
  });

  const isReleaseArtist = authUser?.artist?.id === release.artist.id;

  const {
    onPlayClick: onPlayButtonClick,
    isLoading: isPlayButtonLoading,
    isPlaying,
    currentListeningParty,
  } = usePlayButton({
    isAlbum: releaseType === 'ALBUM',
    trackId: track.id,
    playlist: {
      associationId: release.artist.id,
      type: PlaylistType.Artist,
    },
    listeningParty: release.listeningParty,
    releaseId: release.id,
  });

  const onShareButtonClick = () => {
    trackClickEvent({
      event: 'Share Modal Opened',
      properties: {
        associationId: releaseId,
        associationType: 'Release',
        sourceComponent: 'Song Page Action Bar',
      },
    });
    openModal(ModalType.SHARE, {
      body: <ShareModalRelease releaseId={releaseId} />,
    });
  };

  const feedPostModalRelease = getFragment(FeedPostModalMainSoundFragmentDoc, release);

  const { hasFeedAccess } = useBackendUserPersistedData();

  const hasPostingAccess = hasFeedAccess.type === 'has-access';

  const onPostButtonClick = () => {
    if (!authUser) {
      login();
    } else {
      trackClickEvent({
        event: 'Create Post Clicked',
        properties: {
          sourceComponent: 'Song Page Post Button',
          associationId: release.id,
          associationType: 'Release',
        },
      });

      if (hasPostingAccess) {
        openModal(ModalType.FEED_POST, {
          body: (
            <FeedPostModal
              key={feedPostModalRelease.id}
              sourceLocation="Song Page Post Button"
              preselectedResource={{
                type: 'release',
                release: feedPostModalRelease,
              }}
              channelId={null}
            />
          ),
        });
      } else {
        openModal(ModalType.UNLOCK_FEED_POST, {
          body: (
            <UnlockFeedModal
              key={`unlock-song-feed-${feedPostModalRelease.id}`}
              sourceLocation="Song Page Post Button"
              preselectedResource={{
                type: 'release',
                release: feedPostModalRelease,
              }}
            />
          ),
        });
      }
    }
  };

  const onCommentButtonClick = isReleaseOwner
    ? () => {
        openModal(ModalType.COMMENT_MODAL_V2, {
          body: <CommentModalV2 releaseId={releaseId} />,
        });
      }
    : undefined;

  const moreDropdownItems = (
    <>
      <DisplayToggle media="tablet">
        {isReleaseArtist && release.draftId && showEditRelease && (
          <EditReleaseMenuItem
            disabled={
              !release.auctionContractType ||
              isEditReleaseDisabled({
                releaseType: release.type,
                auctionContractType: release.auctionContractType,
              })
            }
            onClick={async (e: React.MouseEvent<HTMLDivElement>) => {
              if (!release.draftId) return;

              e.stopPropagation();
              e.preventDefault();

              await clearAllLocalData({
                contractAddress: release.contractAddress,
                releaseId: release.id,
              });

              Router.push(editPath.path);
            }}
          />
        )}
      </DisplayToggle>
      <AddToShelfMenuItem releaseId={releaseId} disabled={!hasAudioAccess} source="Song Page" />
      <AddToQueueMenuItem
        trackId={trackId}
        disabled={!trackId || !hasAudioAccess || !!currentListeningParty}
        emitAnalytics={() => {
          trackClickEvent({
            event: 'Add to Queue Clicked',
            properties: {
              associationId: releaseId,
              associationType: 'Release',
              sourceComponent: 'Song Page',
            },
          });
        }}
      />
      {release.canCollectorsDownloadAudio && (
        <DownloadAudioMenuItem trackId={trackId} disabled={!isReleaseOwner} />
      )}
    </>
  );

  return (
    <ActionBarV2
      isLiked={isLiked}
      isLikeButtonDisabled={!hasAudioAccess}
      onLikeButtonClick={onLikeButtonClick}
      onShareButtonClick={onShareButtonClick}
      onPostButtonClick={onPostButtonClick}
      moreDropdownItems={moreDropdownItems}
      isPlaying={isPlaying}
      isPlayButtonLoading={isPlayButtonLoading}
      isPlayButtonDisabled={!hasAudioAccess}
      onPlayButtonClick={onPlayButtonClick}
      onCommentButtonClick={onCommentButtonClick}
      align="start"
      currentListeningParty={currentListeningParty}
    />
  );
}

export function SongView({ release }: { release: FragmentType<SongViewFragmentDoc> }) {
  const { song_tab } = useParams();

  const [activeTab, setActiveTab] = useState<TabChoice>(TAB_CHOICES.OVERVIEW);
  const tabRef = useRef<HTMLDivElement>(null);
  const tabParentRef = useRef<HTMLDivElement>(null);

  const releaseFrag = getFragment(SongViewFragmentDoc, release);

  const { hideAudioPlayer, activeTrackId } = useAudioController();
  useReleasePartyConfetti(releaseFrag);

  useSafeLayoutEffect(() => {
    if (song_tab === TAB_CHOICES.DETAILS) return setActiveTab(TAB_CHOICES.DETAILS);
    if (song_tab === TAB_CHOICES.AUDIENCE) return setActiveTab(TAB_CHOICES.AUDIENCE);
    if (song_tab === TAB_CHOICES.ACTIVITY) return setActiveTab(TAB_CHOICES.ACTIVITY);
    if (song_tab === TAB_CHOICES.COMMENTS) return setActiveTab(TAB_CHOICES.COMMENTS);
    if (song_tab === TAB_CHOICES.OVERVIEW) return setActiveTab(TAB_CHOICES.OVERVIEW);
  }, [song_tab, releaseFrag.track.id]);

  useEffect(() => {
    setQueryData(
      {
        query: WaveformExpandedPlayerDocument,
        variables: {
          releaseId: releaseFrag.id,
        },
      },
      previousData => {
        if (previousData) return previousData;

        return {
          data: {
            release: releaseFrag,
          },
        };
      },
    );
  }, [releaseFrag]);

  useEffect(() => {
    if (!song_tab) {
      setActiveTab(TAB_CHOICES.OVERVIEW);
    }
  }, [song_tab]);

  return (
    <SongPageContainer isAudioPlayerHidden={hideAudioPlayer || !activeTrackId}>
      <SongHero release={releaseFrag} />
      <InfoContainer>
        <LeftContainer>
          <Sections>
            <Wrapper className={clsx('z-[101]')}>
              <SharedWrapper>
                <Actions release={releaseFrag} />
              </SharedWrapper>
              <DisplayToggle media="notLaptop">
                <SharedWrapper>
                  <MintComponent
                    referral={null}
                    releaseData={releaseFrag}
                    collectButtonSource="Mint Component"
                  />
                </SharedWrapper>
              </DisplayToggle>
            </Wrapper>
            <div ref={tabParentRef}>
              <Tabs
                defaultValue={TAB_CHOICES.DETAILS}
                value={activeTab}
                onValueChange={v => (ParamsStore.song_tab = v)}
              >
                <SharedWrapper
                  className={clsx('sticky top-0 z-stickyHeader flex bg-white lt:pt-6')}
                  ref={tabRef}
                >
                  <TabList className="mt-6 overflow-x-auto scrollbar-none lg:mt-8 lt:mt-0">
                    <TabTrigger value={TAB_CHOICES.OVERVIEW} label={TAB_CHOICES.OVERVIEW} />

                    <TabTrigger value={TAB_CHOICES.COMMENTS} label={TAB_CHOICES.COMMENTS} />
                    <TabTrigger value={TAB_CHOICES.AUDIENCE} label={TAB_CHOICES.AUDIENCE} />
                    <TabTrigger value={TAB_CHOICES.ACTIVITY} label={TAB_CHOICES.ACTIVITY} />
                  </TabList>
                </SharedWrapper>
                <TabContent
                  activeTab={activeTab}
                  releaseFrag={releaseFrag}
                  tabRef={tabRef}
                  setActiveTab={setActiveTab}
                  tabParentRef={tabParentRef}
                />
              </Tabs>
            </div>
          </Sections>
        </LeftContainer>
        <RightContainer>
          <MintComponent
            referral={null}
            releaseData={releaseFrag}
            collectButtonSource="Mint Component"
          />
        </RightContainer>
      </InfoContainer>
      <div className="mt-8 grid gap-8 pb-[42px] md:mt-16 md:gap-12 md:pb-16">
        <DiscoveredOnCarousel
          artistFrag={releaseFrag.artist}
          releaseId={releaseFrag.id}
          titleSlug={releaseFrag.titleSlug}
        />
        <PeopleAlsoCollectedCarousel
          artistFrag={releaseFrag.artist}
          releaseId={releaseFrag.id}
          titleSlug={releaseFrag.titleSlug}
        />
      </div>
    </SongPageContainer>
  );
}
